/**
 * Dashboard alert dismissal — 24-hour snooze per alert ID.
 *
 * Will's complaint that triggered this: a red banner appears on the
 * Dashboard, tapping it just sends him into the linked form, and there's
 * no way to make the banner go away if he doesn't want to action it
 * right now. Builders want to be able to override the nag without
 * jumping through hoops.
 *
 * Design:
 * - Dismissed alerts are stored as { [alertId]: expiryTimestamp }
 *   in localStorage.
 * - 24-hour snooze by default — if the same alert id is still firing
 *   tomorrow, it'll show again. That's intentional: these are real
 *   nags (expired certs, no first aider on site, etc.), we don't want
 *   people permanently silencing them and forgetting.
 * - Per-device: localStorage is browser-local, so dismissing on the
 *   gaffer's phone doesn't hide the same banner from a labourer
 *   signed in on the iPad. That's fine for now.
 *
 * If we later want permanent dismiss for "advisory" alerts, add a
 * `permanent` flag to the alert type and route it to IndexedDB or
 * a per-user setting instead.
 */

const STORAGE_KEY = 'hs_dismissed_alerts_v1';
const DEFAULT_SNOOZE_MS = 24 * 60 * 60 * 1000;

type Dismissals = Record<string, number>; // alertId -> expiry timestamp (ms)

function readStore(): Dismissals {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return {};
    return parsed as Dismissals;
  } catch {
    return {};
  }
}

function writeStore(d: Dismissals) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(d));
  } catch {
    /* localStorage full or disabled — silently no-op */
  }
}

/** Returns the set of alert IDs that are currently snoozed. Expired entries
 *  are filtered out (and cleaned up from storage as a side-effect). */
export function getDismissedAlertIds(): Set<string> {
  const now = Date.now();
  const store = readStore();
  const live: Dismissals = {};
  for (const [id, expiry] of Object.entries(store)) {
    if (typeof expiry === 'number' && expiry > now) live[id] = expiry;
  }
  // Compact storage if we cleaned anything up
  if (Object.keys(live).length !== Object.keys(store).length) writeStore(live);
  return new Set(Object.keys(live));
}

/** Dismiss an alert. Re-fires after `snoozeMs` (default 24h). */
export function dismissAlert(alertId: string, snoozeMs: number = DEFAULT_SNOOZE_MS): void {
  const store = readStore();
  store[alertId] = Date.now() + snoozeMs;
  writeStore(store);
}

/** Clear all dismissals — used by a debug action or "show me everything". */
export function clearAllDismissals(): void {
  writeStore({});
}
